<section class="webhooks-page">
  <!-- Top Bar: Standard 76px edge-to-edge header matching system -->
  <header class="page-topbar hero-section">
    <div class="topbar-title-block title-block">
      <h1 class="page-title">Manage Webhooks</h1>
      <p class="page-subtitle helper-copy">
        Configure and manage real-time webhook endpoints to receive signing and document events.
      </p>
    </div>

    <button type="button" class="add-btn btn-purple" (click)="openAddModal()">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span>Add Webhook</span>
    </button>
  </header>

  <div class="webhooks-body">
    <section class="manage-section" aria-labelledby="urls-to-publish-title">
      <!-- Section Heading and Filter Toolbar -->
      <div class="section-heading-row">
        <div class="section-heading">
          <h2 id="urls-to-publish-title">URL's to Publish</h2>
        </div>

        <div class="toolbar">
          <label class="filter-box" aria-label="Filter users with webhooks">
            <svg class="search-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="9" r="5.5"></circle>
              <line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
            </svg>
            <input type="text" [(ngModel)]="filterQuery" placeholder="Filter user(s) with webhooks" />
          </label>

          <div class="total-webhooks-count">
            <strong>{{ filteredWebhooks.length }}</strong>
            <span>{{ filteredWebhooks.length === 1 ? 'Webhook' : 'Webhooks' }}</span>
          </div>
        </div>
      </div>

      @if (feedbackMessage) {
        <div class="alert-banner success">
          <p class="status-note" aria-live="polite">{{ feedbackMessage }}</p>
        </div>
      }

      @if (errorMessage) {
        <div class="alert-banner error">
          <p class="status-note status-note-error" aria-live="polite">{{ errorMessage }}</p>
        </div>
      }

      <!-- Reusable c-table with global 10px rounded thead and floating card rows -->
      <div class="table-container table-card">
        <div class="table-scroll">
          <table class="c-table">
            <thead>
              <tr>
                <th class="name-col">Name</th>
                <th class="key-col">API Key</th>
                <th class="url-col">URL</th>
                <th class="creator-col">Created By</th>
                <th class="state-col">State</th>
              </tr>
            </thead>
            <tbody>
              @if (loading) {
                <tr>
                  <td colspan="5" class="c-table-loading-cell empty-row">
                    <div class="c-loading-state">
                      <div class="c-spinner"></div>
                      <p class="c-loading-text">Loading webhooks…</p>
                    </div>
                  </td>
                </tr>
              } @else if (filteredWebhooks.length === 0) {
                <tr>
                  <td colspan="5" class="empty-row">
                    <div class="empty-state-wrap">
                      <div class="empty-state-icon">
                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                        </svg>
                      </div>
                      <p class="empty-state-text">No webhooks found.</p>
                      <p class="empty-state-sub">Create your first webhook endpoint to automatically receive document updates.</p>
                      <button type="button" class="add-btn btn-purple" (click)="openAddModal()">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>Add Webhook</span>
                      </button>
                    </div>
                  </td>
                </tr>
              } @else {
                @for (webhook of filteredWebhooks; track trackByWebhook($index, webhook)) {
                  <tr class="webhook-row">
                    <td class="name-cell">
                      <div class="webhook-title-wrap">
                        <span class="webhook-name">{{ webhook.name }}</span>
                        <span class="account-label">(Account)</span>
                      </div>
                    </td>
                    <td class="key-cell">
                      <span class="api-key-badge">{{ webhook.apiKeyName }}</span>
                    </td>
                    <td class="url-cell">
                      <span class="url-text" [title]="webhook.url">{{ webhook.url }}</span>
                    </td>
                    <td class="creator-cell">
                      <span class="creator-text">{{ webhook.createdBy }}</span>
                    </td>
                    <td class="state-cell">
                      <div class="state-actions-wrap">
                        <label class="state-switch">
                          <input
                            type="checkbox"
                            [checked]="webhook.active"
                            [disabled]="togglePendingIds.has(webhook.id)"
                            (change)="toggleWebhookState(webhook, $any($event.target).checked)"
                          />
                          <span class="state-switch-track" aria-hidden="true"></span>
                          <span class="state-label">{{ webhook.active ? 'Active' : 'Inactive' }}</span>
                        </label>

                        <div class="row-actions">
                          <button type="button" class="row-action-btn edit-btn" (click)="openEditModal(webhook)">
                            Edit
                          </button>
                          <button type="button" class="row-action-btn row-action-btn--danger delete-btn" (click)="openDeleteConfirm(webhook)">
                            Delete
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        <footer class="table-footer">
          <span class="pagination-count">{{ paginationLabel }}</span>
        </footer>
      </div>
    </section>
  </div>

  <!-- Add / Edit Webhook Modal -->
  @if (modalOpen) {
    <section class="webhook-modal-mask modal-mask modal-mask--open" role="presentation" (click)="closeModal()">
      <div class="webhook-modal modal-card" role="dialog" aria-modal="true" aria-labelledby="webhook-modal-title" (click)="$event.stopPropagation()">
        <header class="webhook-modal-header modal-header">
          <h2 id="webhook-modal-title">{{ currentModalTitle }}</h2>
          <button type="button" class="modal-close-btn" aria-label="Close webhook modal" (click)="closeModal()">×</button>
        </header>

        <div class="webhook-modal-body modal-body">
          <div class="form-grid">
            <label class="form-field">
              <span class="form-label">Name*</span>
              <input type="text" [(ngModel)]="webhookName" placeholder="Enter name..." />
            </label>

            <label class="form-field">
              <span class="form-label">API Key*</span>
              <select [(ngModel)]="selectedApiKeyId">
                <option value="">Select API key</option>
                @for (apiKey of apiKeyOptions; track apiKey.id) {
                  <option [value]="apiKey.id">{{ apiKey.name }} ({{ formatEnvironment(apiKey.environment) }})</option>
                }
              </select>
            </label>
          </div>

          @if (apiKeyLoadError) {
            <p class="inline-note inline-note-error" aria-live="polite">{{ apiKeyLoadError }}</p>
          } @else if (apiKeyOptions.length === 0) {
            <p class="inline-note">Create an API key first to publish webhook events.</p>
          }

          <div class="verify-row">
            <label class="form-field verify-field">
              <span class="form-label">URL to publish (HTTPS required)</span>
              <input
                type="text"
                [ngModel]="publishUrl"
                (ngModelChange)="setPublishUrl($event)"
                placeholder="https://example.com/webhooks"
              />
            </label>

            <button type="button" class="verify-btn btn-purple" [disabled]="!canVerifyWebhook" (click)="verifyPublishUrl()">
              {{ verificationSubmitting ? 'Verifying…' : 'Verify' }}
            </button>
          </div>

          <p class="helper-copy">
            We'll send a verification POST request to this URL and need a <code>200 OK</code> response for verification.
          </p>

          @if (verificationSuccessMessage) {
            <p class="inline-note inline-note-success" aria-live="polite">{{ verificationSuccessMessage }}</p>
          }

          @if (verificationError) {
            <p class="inline-note inline-note-error" aria-live="polite">{{ verificationError }}</p>
          }

          <section class="events-section">
            <label class="checkbox-row checkbox-row-strong">
              <input type="checkbox" [checked]="allEventsSelected" (change)="toggleAllEvents($any($event.target).checked)" />
              <span>Select All Events</span>
            </label>

            <div class="event-groups">
              @for (group of eventGroups; track group.key) {
                <div class="event-group">
                  <button type="button" class="event-group-toggle" (click)="toggleAccordion(group.key)">
                    <span>{{ group.title }} ({{ selectedGroupCount(group) }})</span>
                    <span>{{ group.options.length }} events {{ accordionOpen[group.key] ? '⌃' : '⌄' }}</span>
                  </button>

                  @if (accordionOpen[group.key]) {
                    <div class="event-group-body">
                      <label class="checkbox-row">
                        <input
                          type="checkbox"
                          [checked]="areGroupEventsSelected(group)"
                          (change)="toggleGroupAll(group, $any($event.target).checked)"
                        />
                        <span>Select All</span>
                      </label>

                      <div class="event-options">
                        @for (option of group.options; track option.key) {
                          <label class="checkbox-row">
                            <input
                              type="checkbox"
                              [checked]="eventSelection[option.key]"
                              (change)="setEventSelection(option.key, $any($event.target).checked)"
                            />
                            <span>{{ option.label }}</span>
                          </label>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </section>

          <section class="headers-section">
            <h3>Header</h3>

            <div class="header-actions">
              <button type="button" class="header-link-btn" (click)="addHeader('custom')">+ Add Custom Header</button>
              <button type="button" class="header-link-btn" (click)="addHeader('secret')">+ Add Secret Header</button>
              <button type="button" class="header-link-btn" [disabled]="hasBasicAuthHeader" (click)="addHeader('basic_auth')">
                + Add Basic Auth Header
              </button>
            </div>

            @if (hasBasicAuthHeader) {
              <p class="inline-note">You can add a maximum of 1 basic auth header to the webhook.</p>
            }

            @if (headerDrafts.length > 0) {
              <div class="header-list">
                @for (header of headerDrafts; track trackByHeader($index, header)) {
                  <div class="header-row">
                    <div class="header-inputs">
                      <label class="form-field">
                        <span>{{ header.type === 'basic_auth' ? 'Username' : 'Key' }}</span>
                        <input
                          type="text"
                          [(ngModel)]="header.key"
                          [placeholder]="header.type === 'basic_auth' ? 'Enter username' : 'Enter key'"
                        />
                      </label>

                      <label class="form-field">
                        <span>{{ header.type === 'basic_auth' ? 'Password' : 'Value' }}</span>
                        <input
                          type="text"
                          [(ngModel)]="header.value"
                          [placeholder]="header.type === 'basic_auth' ? 'Enter password' : header.type === 'secret' ? 'Enter secret' : 'Enter value'"
                        />
                      </label>
                    </div>

                    <button type="button" class="remove-header-btn" aria-label="Remove header" (click)="removeHeader(header.id)">×</button>
                  </div>
                }
              </div>
            }
          </section>

          @if (modalError) {
            <p class="inline-note inline-note-error" aria-live="polite">{{ modalError }}</p>
          }
        </div>

        <footer class="webhook-modal-actions modal-footer">
          <button type="button" class="ghost-btn" (click)="closeModal()" [disabled]="modalSubmitting || verificationSubmitting">Cancel</button>
          <button type="button" class="primary-btn btn-purple" [disabled]="!canSaveWebhook" (click)="submitWebhook()">
            {{ submitButtonLabel }}
          </button>
        </footer>
      </div>
    </section>
  }

  <!-- Delete Confirm Modal -->
  @if (deleteTarget) {
    <section class="delete-modal-mask modal-mask modal-mask--open" role="presentation" (click)="closeDeleteConfirm()">
      <div class="delete-modal modal-card" role="dialog" aria-modal="true" aria-labelledby="delete-webhook-title" (click)="$event.stopPropagation()">
        <div class="delete-modal-body modal-body">
          <div class="delete-icon-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </div>
          <h3 id="delete-webhook-title">Delete webhook?</h3>
          <p class="delete-copy">Are you sure you want to permanently delete this webhook? This action cannot be undone.</p>
          <p class="delete-modal-detail"><strong>{{ deleteTarget!.name }}</strong></p>
        </div>

        <footer class="delete-modal-actions modal-footer">
          <button type="button" class="ghost-btn" (click)="closeDeleteConfirm()" [disabled]="deleteSubmitting">Cancel</button>
          <button type="button" class="danger-btn" (click)="confirmDelete()" [disabled]="deleteSubmitting">
            {{ deleteSubmitting ? 'Deleting…' : 'Delete' }}
          </button>
        </footer>
      </div>
    </section>
  }
</section>
